import React, { useEffect, useMemo, useState } from 'react'

import { useMediaQuery } from '@mui/material'
import { formatDate } from 'commons/utils/date'
import {
  Alert,
  Checkbox,
  DSInputType,
  Icon,
  Info,
  Input,
  Popover,
  SlimButton,
  Typography,
} from 'components'
import { TypesValidations, type ValidationOptions } from 'constants/enums'
import type { ProductContextState } from 'container/ProductPage'
import { useFormContext, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { setMaterialFilter } from 'store/ProductContext'
import type { Color } from 'store/ProductContext/productContextTypes'
import { useSelector } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import * as S from './styles'

export const BOX_TYPE = {
  CLOSED: 'FECHADA',
  GRID: 'GRADE',
}

interface ProductDetailProps {
  convertAmount: number
}

function ProductDetail({ convertAmount }: ProductDetailProps) {
  const { materialSelected, filter: materialFilter } = useSelector(({ products }) => products)

  const dispatch = useDispatch()
  const { control, reset } = useFormContext()
  const state = useWatch({
    control: control,
  })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  function resetedSizes(key: string) {
    const size = {} as { [y: string]: number }
    for (const color in state[key]) {
      if (
        ![
          'cd_cent_matr',
          'percentualDesconto',
          'fl_caix_grad',
          'qt_caix_grad',
          'disabledColorsRow',
          'dirtyFieldsByColor',
          'disabled_fl_caix_grad',
        ].includes(key)
      ) {
        size[color] = 0
      }
    }
    return size
  }

  const resetObject = {} as ProductContextState

  for (const key in state) {
    if (
      ![
        'cd_cent_matr',
        'percentualDesconto',
        'fl_caix_grad',
        'qt_caix_grad',
        'disabledColorsRow',
        'dirtyFieldsByColor',
        'disabled_fl_caix_grad',
      ].includes(key)
    ) {
      resetObject[key] = resetedSizes(key)
    } else {
      resetObject[key] = state[key]
    }
  }

  const [filter, setFilter] = React.useState('')
  const [records, setRecords] = useState<Color[]>(materialSelected?.cores)

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined
  const matchLG = useMediaQuery(`(max-width: ${theme.breakpoints.lg}px)`)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleCheckMultiple = (event: React.ChangeEvent<HTMLInputElement>, cd_cor: string) => {
    const newCores = records?.map((cor) => {
      if (cor.cd_cor === cd_cor) {
        return { ...cor, checked: event.target.checked }
      }
      return cor
    })
    setRecords(newCores)
  }

  const handleFilter = () => {
    const arrayRecordDsCor: string[] = []
    records &&
      records.filter((record: Color) => {
        if (record.checked) {
          // TODO: Refactor to use cd_cor instead of ds_cor
          arrayRecordDsCor.push(record.ds_cor.toLowerCase())
        }
        return arrayRecordDsCor
      })
    dispatch(setMaterialFilter(arrayRecordDsCor))
    handleClose()
    setFilter('')
  }

  const clearFilter = () => {
    setFilter('')
    const newCores = records?.map((cor) => {
      return { ...cor, checked: false }
    })
    setRecords(newCores)
    dispatch(setMaterialFilter([]))
    handleClose()
  }

  const getCheckStatus = (cd_cor: string) => {
    let checkStatus = false
    records?.map((cor) => {
      if (cor.cd_cor === cd_cor) {
        checkStatus = cor.checked || false
      }
    })
    return checkStatus
  }

  const updateDate = useMemo(() => {
    const date = new Date()
    const day = formatDate(date, 'DD/MM')
    const time = formatDate(date, 'HH[h]mm')
    return `Dados atualizados em ${day} às ${time}`
  }, [materialSelected])

  useEffect(() => {
    if (materialFilter.length > 0) {
      const newCores = records?.map((cor) => ({ ...cor, checked: true }))
      setRecords(newCores)
    }
  }, [])

  const canShowAlert =
    shouldShowMultiplicityError(
      materialSelected.ds_tipo_caix,
      materialSelected.fl_verf_sku,
      state,
      convertAmount,
    ) && materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED

  const filtererdData = materialSelected?.cores?.filter(
    (cor) =>
      cor.cd_cor?.includes(filter) || cor.ds_cor?.toLowerCase().includes(filter.toLowerCase()),
  )
  const noContent = filtererdData.length <= 0
  const hasCheck = records.some((rec) => rec.checked)
  const mixedBoxView = materialSelected.fl_digi_caix_vari === TypesValidations.YES
  return (
    <>
      <S.ProductDetailContainer mixedBoxView={state.fl_caix_grad} showAlert={canShowAlert}>
        <S.LastUpdateWrapper>
          <span>Digitação de material</span>
          <S.LastUpdateContainer>
            <Typography design="hav-h8-text">{updateDate}</Typography>
          </S.LastUpdateContainer>
        </S.LastUpdateWrapper>
        {canShowAlert && (
          <S.AlertContainer>
            <Alert
              fullWidth={state.fl_caix_grad ? true : matchLG}
              kind="error"
              title={
                state.fl_caix_grad
                  ? `Quantidade de produtos inválida. O número de produtos deve sempre ser exato: ${convertAmount} pares de uma só cor, por caixa.`
                  : `Quantidade inválida. O número digitado deve ser múltiplo de ${convertAmount}.`
              }
            />
          </S.AlertContainer>
        )}
        {state.fl_caix_grad && (
          <S.MixedBoxHelper>
            <strong>Especificações da Caixa:</strong>
            <p>
              A caixa grade variável deve conter exatos {convertAmount} pares. O valor descrito
              entre parênteses “()” exibe o total de pares por tamanho, distribuídos entre a
              quantidade de caixas.
            </p>
          </S.MixedBoxHelper>
        )}

        <S.DetailsContainer>
          <S.TypingBy>
            {!mixedBoxView && (
              <Info iconSize={15} placement="top-end">
                {`Fator de conversão da caixa múltiplo de
                ${materialSelected?.qt_item_conv || '12 ou 24'}.`}
              </Info>
            )}
            <span id="SelectTitle">
              Digitar por {materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED ? 'pares' : 'caixas'}
            </span>
          </S.TypingBy>

          <SlimButton
            content="Limpar campos"
            icon={<Icon icon="clear-all" size="15" />}
            onClick={() => {
              reset({ ...resetObject })
            }}
            type="button"
            variant="white"
          />

          <S.WrapBoxFilter>
            <SlimButton
              content="Filtrar cores"
              icon={<Icon icon="filter" size="15" />}
              id="btn-filter-colors"
              onClick={handleClick}
              type="button"
              variant="white"
            />
            <Popover
              anchorEl={anchorEl as null | undefined}
              id={id}
              onClose={handleClose}
              open={open}
            >
              <S.ContentPopoverHeader>
                <Input
                  addorment
                  design={DSInputType.SEARCH_INPUT}
                  InputProps={{
                    fullWidth: true,
                    placeholder: 'Buscar por cor',
                  }}
                  id="input-filter-colors"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilter(event.target.value)
                  }
                  value={filter}
                />
              </S.ContentPopoverHeader>
              <S.ContentWrapperPopover>
                <S.ContentPopover>
                  {!noContent ? (
                    filtererdData.map((item) => {
                      return (
                        <div key={item.cd_cor}>
                          <Checkbox
                            checked={getCheckStatus(item.cd_cor)}
                            id={`checkbox-filter-colors-${item.cd_cor}`}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              handleCheckMultiple(event, item.cd_cor)
                            }
                            size="small"
                          />
                          <span id="CheckboxItem">
                            {item.cd_cor} | {item.ds_cor}
                          </span>
                        </div>
                      )
                    })
                  ) : (
                    <S.NoResultFindBox>
                      <Icon icon="search-off" size="42" />
                      <span id="NoItemFoundTitle">Nenhum resultado encontrado</span>
                      <span id="NoItemFoundDescription">
                        Confira o que digitou e tente novamente.
                      </span>
                    </S.NoResultFindBox>
                  )}
                </S.ContentPopover>
              </S.ContentWrapperPopover>
              <S.DividerStyled />
              <S.ContentPopoverButton>
                <SlimButton
                  content="Limpar filtro"
                  disabled={noContent || !hasCheck}
                  id="btn-clear-filter-colors"
                  onClick={clearFilter}
                  variant="white"
                />
                <SlimButton
                  content="Aplicar filtro"
                  disabled={noContent || !hasCheck}
                  id="btn-apply-filter-colors"
                  onClick={handleFilter}
                  variant="primary"
                />
              </S.ContentPopoverButton>
            </Popover>
          </S.WrapBoxFilter>
        </S.DetailsContainer>
      </S.ProductDetailContainer>
    </>
  )
}

export default ProductDetail

export function shouldShowMultiplicityError(
  boxType: string,
  validation: ValidationOptions,
  state: ProductContextState,
  convertAmount: number,
) {
  if (Array.isArray(state?.dirtyFieldsByColor) && state.fl_caix_grad) {
    if (!state?.dirtyFieldsByColor.length) return false
    const dirtyField = state.dirtyFieldsByColor[0] as unknown as string
    const dirtyColorValues = Object.entries(state).filter((item) => item[0] === dirtyField)[0][1]
    const rowValue = Object.values(dirtyColorValues).reduce((acc, curr) => {
      return Number(curr) + acc
    }, 0)

    return rowValue !== convertAmount
  }
  if (validation === TypesValidations.YES && boxType === BOX_TYPE.CLOSED) {
    return Object.values(state)
      .filter((val) => !Array.isArray(val) && typeof val === 'object')
      .flatMap((color) => Object.values(color))
      .some((value) => Number(value) % convertAmount !== 0)
  }
  if (validation === TypesValidations.NO && boxType === BOX_TYPE.CLOSED) {
    return Object.values(state)
      .filter((val) => !Array.isArray(val) && typeof val === 'object')
      .some((value) => {
        const rowValue = Object.values(value).reduce((acc, curr) => {
          return Number(curr) + acc
        }, 0)
        return rowValue % convertAmount !== 0
      })
  }

  return false
}
